import { getApiErrorStatus } from "@/lib/ky/error-message";

/**
 * Why a Google round trip failed, as fixed codes rather than the backend's own text. The reason
 * travels in a query parameter and is rendered into a page, so the set of things it can say stays
 * closed — the alternative is echoing arbitrary text from a URL back to the user.
 */
export type GoogleErrorCode = "mismatch" | "conflict" | "expired" | "unknown";

export const googleErrorCodeFor = (error: unknown): GoogleErrorCode => {
  switch (getApiErrorStatus(error)) {
    /* The invited address and the Google account are different people. */
    case 403:
      return "mismatch";
    /* That Google account already backs a different user. */
    case 409:
      return "conflict";
    /* A stale state, a nonce that never matched this browser, or an unverified Google email. */
    case 401:
      return "expired";
    default:
      return "unknown";
  }
};

/** Kept in one place so the sign-in and settings screens say the same thing about the same failure. */
export const googleErrorCopy: Record<GoogleErrorCode, string> = {
  mismatch: "That Google account doesn't match the invited email address.",
  conflict: "That Google account is already connected to another user.",
  expired: "That sign-in link expired or didn't start in this browser. Please try again.",
  unknown: "Google sign-in failed. Please try again.",
};

/**
 * Reads the `?error=` (and optional `?message=`) query params. The backend's own message wins
 * when present — it's more specific than the fixed copy — falling back to the generic reason for
 * `value`, and to nothing at all when there's no error to report.
 */
export const googleErrorMessage = (
  value: string | null,
  message?: string | null,
): string | null => {
  if (message) return message;
  if (!value) return null;

  return googleErrorCopy[value as GoogleErrorCode] ?? googleErrorCopy.unknown;
};
